import { ref } from 'vue'

import { defineStore } from 'pinia'

import { registryApi } from '@/apis/registry.api'
import { getLocalizedItems, getTranslatedItems } from '@/plugins/i18n'
import { useGetOrFetchAsync } from '@/composables/store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useGarageStore } from '@/stores/garage.store'
import { useSaleStore } from '@/stores/sale.store'
import { useOrderStore } from '@/stores/order.store'

export const useRegistryStore = defineStore('registry', () => {
  const webSiteStore = useWebSiteStore()
  const portalStore = usePortalStore()

  const api = registryApi
  const branches = ref([])
  const buyerTypes = ref([])
  const channels = ref([])
  const channelTypes = ref([])
  const companyData = ref({})
  const contactTypes = Object.freeze({ CUSTOMER: 0, SUPPLIER: 1 })
  const customers = ref([])
  const customerElectronicInvoiceAgreementTypes = ref([])
  const customerTypes = ref([])
  const dataControllerDataTypes = Object.freeze({
    IDENTIFICATION_DATA: 2 ** 0,
    CONTACT_DATA: 2 ** 1,
    BANKING_DATA: 2 ** 2,
    TAX_DATA: 2 ** 3,
    PREFERENCES_DATA: 2 ** 4,
    INTERESTS_DATA: 2 ** 5,
    EMPLOYMENT_DATA: 2 ** 6,
    EDUCATION_DATA: 2 ** 7,
    WORK_EXPERIENCE_DATA: 2 ** 8,
    CV_DATA: 2 ** 9
  })
  const dataControllerProcessingMethods = Object.freeze({
    ANONYMOUS_AGGREGATED: 2 ** 0,
    AUTOMATED_SYSTEM: 2 ** 1,
    NON_AUTOMATED_SYSTEM: 2 ** 2,
  })
  const dataControllerLegalBasis = Object.freeze({
    ART6A: 2 ** 0,
    ART6B: 2 ** 1,
    ART6C: 2 ** 2,
    ART6D: 2 ** 3,
    ART6E: 2 ** 4,
    ART6F: 2 ** 5
  })
  const dataControllerDataTransferMechanisms = Object.freeze({
    ADEQUACY_DECISION: 2 ** 0,
    APPROPRIATE_SAFEGUARDS: 2 ** 1,
    EXPLICIT_CONSENT: 2 ** 2,
    ARTICLE_49_DEROGATIONS: 2 ** 3
  })
  const deposits = ref([])
  const discountCategories = ref([])
  const discountCategoryAccountings = ref([])
  const discountLists = ref([])
  const documentSeries = ref([])
  const documentFamilies = Object.freeze({ SALE: 0, CUSTOMER_ORDER: 1, SUPPLIER_ORDER: 2, PURCHASE_DOCUMENT: 3, STOCK: 4, CONTRACT: 5, WORKSHOP: 6 })
  const getDocumentTypesByFamily = (family) => {
    switch (family) {
      case documentFamilies.SALE: return [portalStore.documentTypesEnum.SALE_CORRESPONDENT, portalStore.documentTypesEnum.SALE_DDT, portalStore.documentTypesEnum.SALE_INVOICE, portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE, portalStore.documentTypesEnum.SALE_FISCAL_RECEIPT, portalStore.documentTypesEnum.SALE_CREDIT_NOTE]
      case documentFamilies.SUPPLIER_ORDER: return [portalStore.documentTypesEnum.ORDER_SUPPLIER_ORDER]
      case documentFamilies.WORKSHOP: return [portalStore.documentTypesEnum.GARAGE_QUOTE, portalStore.documentTypesEnum.GARAGE_EVENT, portalStore.documentTypesEnum.GARAGE_WORKSHEET]
      default: return []
    }
  }
  const getReasonsByDocumentFamily = (family) => {
    const saleStore = useSaleStore()
    const orderStore = useOrderStore()
    const garageStore = useGarageStore()
    switch (family) {
      case documentFamilies.SALE: return saleStore.getLocalizedSaleReasons()
      case documentFamilies.SUPPLIER_ORDER: return orderStore.getLocalizedSupplierOrderReasons()
      case documentFamilies.WORKSHOP: return garageStore.getLocalizedGarageReasons()
      default: return []
    }
  }
  const documentTypes = Object.freeze({ SUPPLIER_ORDER: 0, QUOTE: 1, WORKSHEET: 2 })
  const identityDocumentAuthorities = ref([])
  const identityDocumentTypes = ref([])
  const jobTypes = ref([])
  const packagingTypes = ref([])
  const lastUpdateDictionary = ref({})
  const privacyModuleTypes = Object.freeze({ CONTACT: 0, CUSTOMER: 1 })
  const productAccountings = ref([])
  const productTypes = Object.freeze({
    PRODUCT: 0,
    SERVICE: 2,
    TEXT: 3,
    LABOR: 4,
    EXPENSE: 5
  })
  const productFieldMaskFlag = Object.freeze({
    NAME: 1,
    DISCOUNT_CATEGORY: 2,
    COST: 4,
    PRICE: 8,
    PRODUCT_FAMILY: 16,
    UNIT_OF_MEASURE: 32,
    VAT_TYPE: 64,
    WEIGHT_KG: 128,
    INTERNAL_PRICE: 256,
    WARRANTY_PRICE: 512
  })
  const productFamilies = ref([])
  const productTieTypes = Object.freeze({ SUPERSESSION: 0 })
  const productAvailabilities = ref([
    { id: 0, name: 'common.productAvailability.notAvailable' },
    { id: 1, name: 'common.productAvailability.inElimination' },
    { id: 2, name: 'common.productAvailability.new' },
  ])

  const productAvailabilitiesEnum = Object.freeze({
    NOT_AVAILABLE: 0,
    IN_ELIMINATION: 1,
    NEW: 2,
  })
  const subjectContacts = ref([])
  const subjectDiscountEditableFieldMaskFlag = Object.freeze({
    SUPPLIER_BRAND_REF: 1 << 0, // 1
    DISCOUNT_CATEGORY_REF: 1 << 1, // 2
    CUSTOMER_ID: 1 << 2, // 4
    SUPPLIER_REF: 1 << 3, // 8
    DISCOUNT_LIST_REF: 1 << 4, // 16
    PRICE_RANGE: 1 << 5, // 32
    IS_URGENT: 1 << 6, // 64
  })
  const subjectDiscounts = ref([])
  const subjectDiscountTypes = Object.freeze({ SELL: 0, BUY: 1 })
  const subjectEntityTypes = Object.freeze({ SUPPLIER: 1, CUSTOMER: 2, CUSTOMER_SUPPLIER: 3 })
  const subjectTypes = ref([])
  const supplierBrands = ref([])
  const supplierDivisionBrandBehavior = Object.freeze({ DIV_DIV: 0, DIV_SUP: 1, SUP_DIV: 2, SUP_SUP: 3 })
  const supplierDivisions = ref([])
  const suppliers = ref([])
  const supplierDivisionTypes = ref([])
  const supplierOrderTypes = ref([])
  const supplierTypes = ref([])
  const vehicles = ref([])
  const infoBikeBrands = ref([])
  const infoBikeModels = ref({})
  const infoBikeVariants = ref({})
  const vehicleEmissionStandardTypesEnum = Object.freeze({
    EURO_0: 0,
    EURO_1: 1,
    EURO_2: 2,
    EURO_3: 3,
    EURO_4: 4,
    EURO_5: 5,
    EURO_6: 6
  })
  const vehicleFuelTypesEnum = Object.freeze({
    GASOLINE: 0,
    DIESEL: 1,
    ELECTRIC: 2,
    HYBRID: 3,
    SEPARATE_LUBRICATION: 4,
    MIXED_OIL: 5,
    LPG: 6,
    METHANE: 7,
    OTHER: 8
  })
  const vehicleMovementTypesEnum = Object.freeze({
    NEW_PURCHASE: 0,
    OWNERSHIP_TRANSFER: 1,
    USED_ITEM_PICKUP: 2,
    PICKUP_RESERVATION: 3,
    SLOT_TRANSFER: 4,
    DEMO_ASSIGNMENT: 5,
    ON_DISPLAY: 6,
    ZERO_MILEAGE_ASSIGNMENT: 7,
    RENTAL_ASSIGNMENT: 8,
    RESERVATION: 9,
    RETURN_TO_SUPPLIER: 10,
    SCRAPPING: 11,
    SALE_TO_CUSTOMER: 12,
    SALE_TO_RESELLER: 13
  })
  const vehicleStatusTypesEnum = Object.freeze({
    NEW: 0,
    KM0: 1,
    DEMO: 2,
    USED: 3,
    RENTED: 4,
    SOLD: 5,
    TRANSITED: 6,
    SCRAPPED: 7,
    VIRTUAL_NEW: 8,
    VIRTUAL_USED: 9,
  })
  const vehicleTransmissionTypesEnum = Object.freeze({
    MECHANIC: 0,
    AUTOMATIC: 1,
    SEQUENTIAL: 2,
    DIRECT: 3,
    CVT: 4,
    DUAL_CLUTCH: 5,
    OTHER: 6,
  })

  const fetchLastUpdateAsync = async() => registryApi.getLastUpdateAsync()

  const fetchBranchesAsync = async(force = false, params) => useGetOrFetchAsync(branches, () => registryApi.getBranchesAsync(params), force)
  const fetchBuyerTypesAsync = async(force = false, params) => useGetOrFetchAsync(buyerTypes, () => registryApi.getBuyerTypesAsync(params), force)
  const fetchChannelsAsync = async(force = false, params) => useGetOrFetchAsync(channels, () => registryApi.getChannelsAsync(params), force)
  const fetchChannelTypesAsync = async(force = false, params) => useGetOrFetchAsync(channelTypes, () => registryApi.getChannelTypesAsync(params), force)
  const fetchCompanyDataAsync = async(force = false, params) => useGetOrFetchAsync(companyData, () => registryApi.getCompanyDataAsync(params), force)
  const fetchCustomerElectronicInvoiceAgreementTypesAsync = async(force = false, params) => useGetOrFetchAsync(customerElectronicInvoiceAgreementTypes, () => registryApi.getCustomerElectronicInvoiceAgreementTypesAsync(params), force)
  const fetchCustomersAsync = async(force = false, params) => {
    if (!customers.value?.length || force) {
      const res = await registryApi.getCustomersAsync(params)
      const data = Array.isArray(res.data) ? res.data : (res.data?.value ?? [])
      customers.value.splice(0, customers.value.length, ...data)
    }
    return customers.value
  }
  const fetchCustomerTypesAsync = async(force = false, params) => useGetOrFetchAsync(customerTypes, () => registryApi.getCustomerTypesAsync(params), force)
  const fetchDepositsAsync = async(force = false, params) => useGetOrFetchAsync(deposits, () => registryApi.getDepositsAsync(params), force)
  const fetchDiscountCategoriesAsync = async(force = false, params) => useGetOrFetchAsync(discountCategories, () => registryApi.getDiscountCategoriesAsync(params), force)
  const fetchDiscountCategoryAccountingsAsync = async(force = false, params) => useGetOrFetchAsync(discountCategoryAccountings, () => registryApi.getDiscountCategoryAccountingsAsync(params), force)
  const fetchDiscountListsAsync = async(force = false, params) => useGetOrFetchAsync(discountLists, () => registryApi.getDiscountListsAsync(params), force)
  const fetchDocumentSeriesAsync = async(force = false, params) => useGetOrFetchAsync(documentSeries, () => registryApi.getDocumentSeriesAsync(params), force)
  const fetchIdentityDocumentAuthoritiesAsync = async(force = false, params) => useGetOrFetchAsync(identityDocumentAuthorities, () => registryApi.getIdentityDocumentAuthoritiesAsync(params), force)
  const fetchIdentityDocumentTypesAsync = async(force = false, params) => useGetOrFetchAsync(identityDocumentTypes, () => registryApi.getIdentityDocumentTypesAsync(params), force)
  const fetchJobTypesAsync = async(force = false, params) => useGetOrFetchAsync(jobTypes, () => registryApi.getJobTypesAsync(params), force)
  const fetchPackagingTypesAsync = async(force = false, params) => useGetOrFetchAsync(packagingTypes, () => registryApi.getPackagingTypesAsync(params), force)
  const fetchProductByIdAsync = async(id, params) => registryApi.getProductByIdAsync(id, params)
  const fetchSubjectContactsAsync = async(force = false, params) => useGetOrFetchAsync(subjectContacts, () => registryApi.getSubjectContactsAsync(params), force)
  const fetchSubjectDiscountsAsync = async(force = false, params) => useGetOrFetchAsync(subjectDiscounts, () => registryApi.getSubjectDiscountsAsync(params), force)
  const fetchProductAccountingsAsync = async(force = false, params) => useGetOrFetchAsync(productAccountings, () => registryApi.getProductAccountingsAsync(params), force)
  const fetchProductFamiliesAsync = async(force = false, params) => useGetOrFetchAsync(productFamilies, () => registryApi.getProductFamiliesAsync(params), force)
  const fetchSubjectTypesAsync = async(force = false, params) => useGetOrFetchAsync(subjectTypes, () => registryApi.getSubjectTypesAsync(params), force)
  const fetchSupplierBrandsAsync = async(force = false, params) => useGetOrFetchAsync(supplierBrands, () => registryApi.getSupplierBrandsAsync(params), force)
  const fetchSupplierDivisionsAsync = async(force = false, params) => useGetOrFetchAsync(supplierDivisions, () => registryApi.getSupplierDivisionsAsync(params), force)
  const fetchSupplierDivisionTypesAsync = async(force = false, params) => useGetOrFetchAsync(supplierDivisionTypes, () => registryApi.getSupplierDivisionTypesAsync(params), force)
  const fetchSupplierOrderTypesAsync = async(force = false, params) => useGetOrFetchAsync(supplierOrderTypes, () => registryApi.getSupplierOrderTypesAsync(params), force)
  const fetchSuppliersAsync = async(force = false, params) => useGetOrFetchAsync(suppliers, () => registryApi.getSuppliersAsync(params), force)
  const fetchSupplierTypesAsync = async(force = false, params) => useGetOrFetchAsync(supplierTypes, () => registryApi.getSupplierTypesAsync(params), force)
  const fetchVehiclesAsync = async(force = false, params) => useGetOrFetchAsync(vehicles, () => registryApi.getVehiclesAsync(null, params), force)
  const fetchInfoBikeBrandsAsync = async(force = false, params) => useGetOrFetchAsync(infoBikeBrands, () => registryApi.getInfoBikeBrandsAsync(params), force)
  const fetchInfoBikeModelsAsync = async(brand_code, force = false) => {
    if (!brand_code) return []
    if (infoBikeModels.value[brand_code]?.length && !force) {
      return infoBikeModels.value[brand_code]
    }
    const res = await api.getInfoBikeModelsAsync({ brand_code })
    if (res.data) {
      infoBikeModels.value[brand_code] = res.data
    }
    return infoBikeModels.value[brand_code] ?? []
  }
  const fetchInfoBikeVariantsAsync = async(model_code, force = false) => {
    if (!model_code) return []
    if (infoBikeVariants.value[model_code]?.length && !force) {
      return infoBikeVariants.value[model_code]
    }
    const res = await api.getInfoBikeVariantsAsync({ model_code })
    if (res.data) {
      infoBikeVariants.value[model_code] = res.data
    }
    return infoBikeVariants.value[model_code] ?? []
  }

  const getLocalizedBranches = () => getTranslatedItems(branches.value)
  const getLocalizedBuyerTypes = () => getTranslatedItems(buyerTypes.value)
  const getLocalizedChannels = () => getTranslatedItems(channels.value)
  const getLocalizedChannelTypes = () => getTranslatedItems(channelTypes.value)
  const getLocalizedCustomerElectronicInvoiceAgreementTypes = () => getTranslatedItems(customerElectronicInvoiceAgreementTypes.value)
  const getLocalizedCustomerTypes = () => getTranslatedItems(customerTypes.value)
  const getLocalizedDeposits = () => getTranslatedItems(deposits.value)
  const getLocalizedDiscountLists = () => getTranslatedItems(discountLists.value)
  const getLocalizedDiscountCategories = () => getTranslatedItems(discountCategories.value).map(x => ({ ...x, name: `${x.supplier_brand_ref_code} - ${x.name}` }))
  const getLocalizedDocumentFamilies = () => getLocalizedItems(Object.keys(documentFamilies).map(x => ({
    id: documentFamilies[x],
    name: `common.documentFamily.${x.toLowerCase()}`,
  })))
  const getLocalizedDocumentTypes = () => getLocalizedItems(Object.keys(documentTypes).map(x => ({
    id: documentTypes[x],
    name: `common.documentType.${x.toLowerCase()}`,
    color: documentTypes[x] === documentTypes.SUPPLIER_ORDER ? 'blue' : documentTypes[x] === documentTypes.QUOTE ? 'green' : 'orange',
  })))
  const getLocalizedIdentityDocumentAuthorities = () => getTranslatedItems(identityDocumentAuthorities.value)
  const getLocalizedIdentityDocumentTypes = () => getTranslatedItems(identityDocumentTypes.value)
  const getLocalizedJobTypes = () => getTranslatedItems(jobTypes.value)
  const getLocalizedPackagingTypes = () => getTranslatedItems(packagingTypes.value)
  const getLocalizedProductFamilies = () => getTranslatedItems(productFamilies.value)
  const getLocalizedProductAvailabilities = () => getLocalizedItems(productAvailabilities.value)
  const getLocalizedProductTieTypes = () => getLocalizedItems(Object.keys(productTieTypes).map(x => ({
    id: productTieTypes[x],
    name: `common.productTieType.${x.toLowerCase()}`,
    color: '#ffcc4b',
  })))
  const getLocalizedProductTypes = () => getLocalizedItems(Object.keys(productTypes).map(x => {
    const colors = {
      product: '#2196f3', // Blue 500
      service: '#4caf50', // Green 500
      text: '#607d8b', // Blue Grey 500
      labor: '#ff9800', // Orange 500
      expense: '#f44336', // Red 500
    }
    return {
      id: productTypes[x],
      name: `common.productTypes.${x.toLowerCase()}`,
      color: colors[x.toLowerCase()]
    }
  }))
  const getLocalizedSubjectTypes = () => getTranslatedItems(subjectTypes.value)
  const getLocalizedSubjectEntityTypes = () => getLocalizedItems(Object.keys(subjectEntityTypes).map(x => ({
    id: subjectEntityTypes[x],
    name: `common.subjectEntityType.${x.toLowerCase()}`,
    color: subjectEntityTypes[x] === subjectEntityTypes.SUPPLIER ? 'blue' : subjectEntityTypes[x] === subjectEntityTypes.CUSTOMER ? 'green' : 'orange',
  })))
  const getLocalizedSupplierDivisionTypes = () => getTranslatedItems(supplierDivisionTypes.value)
  const getLocalizedSupplierOrderTypes = () => getTranslatedItems(supplierOrderTypes.value)
  const getLocalizedSupplierTypes = () => getTranslatedItems(supplierTypes.value)
  const getVehicles = () => {
    const vehicles_res = vehicles.value.map(v => ({ name: v.name, id: v.id }))
    return vehicles_res
  }
  const getOrFetchCustomersAsync = async() => {
    await webSiteStore.fetchEntityLastUpdateAsync(useRegistryStore(), () => registryApi.getLastUpdate(registryApi.customersUrl))
    return customers.value
  }
  const getOrFetchSubjectContactsAsync = async(onlyCustomer = false) => {
    await webSiteStore.fetchEntityLastUpdateAsync(useRegistryStore(), () => registryApi.getLastUpdate(registryApi.subjectContactsUrl))
    if (onlyCustomer) {
      return subjectContacts.value.map(i => {
        return { ...i, id: Math.abs(i.id) }
      })
    }
    return subjectContacts.value
  }
  const getOrFetchVehiclesAsync = async() => {
    await webSiteStore.fetchEntityLastUpdateAsync(useRegistryStore(), () => registryApi.getLastUpdate(registryApi.vehiclesUrl))
    return vehicles.value
  }
  const getLocalizedDataControllerDataTypes = () => getLocalizedItems(Object.keys(dataControllerDataTypes).map(x => ({
    id: dataControllerDataTypes[x],
    name: `common.companyData.privacy.dataControllerDataType.${x.toLowerCase()}`
  })))
  const getLocalizedDataControllerProcessingMethods = () => getLocalizedItems(Object.keys(dataControllerProcessingMethods).map(x => ({
    id: dataControllerProcessingMethods[x],
    name: `common.companyData.privacy.dataControllerProcessingMethod.${x.toLowerCase()}`
  })))
  const getLocalizedDataControllerLegalBasis = () => getLocalizedItems(Object.keys(dataControllerLegalBasis).map(x => ({
    id: dataControllerLegalBasis[x],
    name: `common.companyData.privacy.dataControllerLegalBasis.${x.toLowerCase()}`
  })))
  const getLocalizedDataControllerDataTransferMechanisms = () => getLocalizedItems(Object.keys(dataControllerDataTransferMechanisms).map(x => ({
    id: dataControllerDataTransferMechanisms[x],
    name: `common.companyData.privacy.dataControllerDataTransferMechanism.${x.toLowerCase()}`
  })))
  const getLocalizedVehicleEmissionStandardTypes = () => getLocalizedItems(Object.keys(vehicleEmissionStandardTypesEnum).map(x => ({
    id: vehicleEmissionStandardTypesEnum[x],
    name: `common.vehicleEmissionStandardType.${x.toLowerCase()}`
  })))
  const getLocalizedVehicleFuelTypes = () => getLocalizedItems(Object.keys(vehicleFuelTypesEnum).map(x => ({
    id: vehicleFuelTypesEnum[x],
    name: `common.vehicleFuelType.${x.toLowerCase()}`
  })))
  const getLocalizedVehicleMovementTypes = () => getLocalizedItems(Object.keys(vehicleMovementTypesEnum).map(x => ({
    id: vehicleMovementTypesEnum[x],
    name: `common.vehicleMovementType.${x.toLowerCase()}`
  })))
  const getLocalizedVehicleStatusTypes = () => getLocalizedItems(Object.keys(vehicleStatusTypesEnum).map(x => ({
    id: vehicleStatusTypesEnum[x],
    name: `common.vehicleStatusType.${x.toLowerCase()}`
  })))
  const getLocalizedVehicleTransmissionTypes = () => getLocalizedItems(Object.keys(vehicleTransmissionTypesEnum).map(x => ({
    id: vehicleTransmissionTypesEnum[x],
    name: `common.vehicleTransmissionType.${x.toLowerCase()}`
  })))
  const getDefaultSupplierBrand = () => supplierBrands.value.find(x => x.code === '$$')
  const getSupplierBrandById = (id) => supplierBrands.value.find(x => x.id === id)
  const getProductDetailsAsync = async(id) => registryApi.getProductDetailsAsync(id)
  const getSubjects = () => {
    const subjects = suppliers.value.map(s => ({ name: s.name, code: s.code, id: s.subject_ref }))

    const subject_ids = subjects.map(s => s.id)
    customers.value.filter(c => !subject_ids.includes(-c.subject)).forEach(e => {
      subjects.push({ name: e.name, code: e.code, id: -e.subject })
    })
    subjects.sort((a, b) => a.code?.localeCompare(b.code))
    return subjects
  }

  const setCompanyData = (data) => { companyData.value = data }
  const updateCustomerPrivacyModuleAsync = async(id, data) => registryApi.updateCustomerPrivacyModuleAsync(id, data)

  const $reset = () => {
    branches.value = []
    buyerTypes.value = []
    channels.value = []
    channelTypes.value = []
    companyData.value = {}
    customers.value = []
    customerElectronicInvoiceAgreementTypes.value = []
    customerTypes.value = []
    deposits.value = []
    discountCategories.value = []
    discountCategoryAccountings.value = []
    identityDocumentAuthorities.value = []
    identityDocumentTypes.value = []
    infoBikeBrands.value = []
    infoBikeModels.value = {}
    infoBikeVariants.value = {}
    jobTypes.value = []
    packagingTypes.value = []
    productAccountings.value = []
    productFamilies.value = []
    subjectContacts.value = []
    subjectDiscounts.value = []
    subjectTypes.value = []
    supplierBrands.value = []
    supplierDivisions.value = []
    supplierDivisionTypes.value = []
    supplierOrderTypes.value = []
    supplierTypes.value = []
    suppliers.value = []
    vehicles.value = []
  }

  return {
    $reset,
    api,
    branches,
    buyerTypes,
    channels,
    channelTypes,
    companyData,
    contactTypes,
    customers,
    customerTypes,
    dataControllerDataTypes,
    dataControllerProcessingMethods,
    dataControllerLegalBasis,
    dataControllerDataTransferMechanisms,
    deposits,
    discountCategories,
    discountCategoryAccountings,
    discountLists,
    documentFamilies,
    getDocumentTypesByFamily,
    getReasonsByDocumentFamily,
    documentTypes,
    documentSeries,
    identityDocumentAuthorities,
    identityDocumentTypes,
    infoBikeBrands,
    infoBikeModels,
    infoBikeVariants,
    jobTypes,
    packagingTypes,
    lastUpdateDictionary,
    privacyModuleTypes,
    productAccountings,
    productFamilies,
    productTypes,
    productTieTypes,
    productAvailabilities,
    productAvailabilitiesEnum,
    productFieldMaskFlag,
    subjectContacts,
    subjectDiscountTypes,
    subjectDiscountEditableFieldMaskFlag,
    subjectEntityTypes,
    subjectDiscounts,
    subjectTypes,
    supplierBrands,
    supplierDivisionBrandBehavior,
    supplierDivisions,
    supplierDivisionTypes,
    supplierOrderTypes,
    supplierTypes,
    suppliers,
    vehicles,
    vehicleFuelTypesEnum,
    vehicleMovementTypesEnum,
    vehicleStatusTypesEnum,
    fetchBranchesAsync,
    fetchBuyerTypesAsync,
    fetchChannelsAsync,
    fetchChannelTypesAsync,
    fetchCompanyDataAsync,
    fetchCustomerElectronicInvoiceAgreementTypesAsync,
    fetchCustomersAsync,
    fetchCustomerTypesAsync,
    fetchDepositsAsync,
    fetchDiscountCategoriesAsync,
    fetchDiscountCategoryAccountingsAsync,
    fetchDiscountListsAsync,
    fetchDocumentSeriesAsync,
    fetchIdentityDocumentAuthoritiesAsync,
    fetchIdentityDocumentTypesAsync,
    fetchJobTypesAsync,
    fetchPackagingTypesAsync,
    fetchLastUpdateAsync,
    fetchProductAccountingsAsync,
    fetchProductByIdAsync,
    fetchProductFamiliesAsync,
    fetchSubjectContactsAsync,
    fetchSubjectDiscountsAsync,
    fetchSubjectTypesAsync,
    fetchSupplierBrandsAsync,
    fetchSupplierDivisionsAsync,
    fetchSupplierDivisionTypesAsync,
    fetchSupplierOrderTypesAsync,
    fetchSuppliersAsync,
    fetchSupplierTypesAsync,
    fetchVehiclesAsync,
    fetchInfoBikeBrandsAsync,
    fetchInfoBikeModelsAsync,
    fetchInfoBikeVariantsAsync,
    getDefaultSupplierBrand,
    getOrFetchCustomersAsync,
    getOrFetchSubjectContactsAsync,
    getOrFetchVehiclesAsync,
    getLocalizedBranches,
    getLocalizedBuyerTypes,
    getLocalizedChannels,
    getLocalizedChannelTypes,
    getLocalizedCustomerElectronicInvoiceAgreementTypes: getLocalizedCustomerElectronicInvoiceAgreementTypes,
    getLocalizedCustomerTypes,
    getLocalizedDataControllerDataTypes,
    getLocalizedDataControllerProcessingMethods,
    getLocalizedDataControllerLegalBasis,
    getLocalizedDataControllerDataTransferMechanisms,
    getLocalizedDeposits,
    getLocalizedDiscountCategories,
    getLocalizedDiscountLists,
    getLocalizedDocumentFamilies,
    getLocalizedDocumentTypes,
    getLocalizedIdentityDocumentAuthorities,
    getLocalizedIdentityDocumentTypes,
    getLocalizedJobTypes,
    getLocalizedPackagingTypes,
    getLocalizedProductAvailabilities,
    getLocalizedProductFamilies,
    getLocalizedProductTypes,
    getLocalizedProductTieTypes,
    getLocalizedSubjectEntityTypes,
    getLocalizedSubjectTypes,
    getLocalizedSupplierDivisionTypes,
    getLocalizedSupplierOrderTypes,
    getLocalizedSupplierTypes,
    getLocalizedVehicleEmissionStandardTypes,
    getLocalizedVehicleFuelTypes,
    getLocalizedVehicleTransmissionTypes,
    getLocalizedVehicleMovementTypes,
    getLocalizedVehicleStatusTypes,
    getProductDetailsAsync,
    getSupplierBrandById,
    getSubjects,
    getVehicles,
    setCompanyData,
    updateCustomerPrivacyModuleAsync,
  }
}, {
  persist: [
    {
      pick: [
        'branches',
        'buyerTypes',
        'channels',
        'channelTypes',
        'companyData',
        'customerElectronicInvoiceAgreementTypes',
        'customers',
        'customerTypes',
        'deposits',
        'discountCategories',
        'discountCategoryAccountings',
        'discountLists',
        'documentSeries',
        'identityDocumentAuthorities',
        'identityDocumentTypes',
        'infoBikeBrands',
        'infoBikeModels',
        'infoBikeVariants',
        'jobTypes',
        'packagingTypes',
        'lastUpdateDictionary',
        'productAccountings',
        'productFamilies',
        'subjectContacts',
        'subjectDiscounts',
        'subjectTypes',
        'supplierBrands',
        'supplierDivisions',
        'supplierDivisionTypes',
        'supplierOrderTypes',
        'suppliers',
        'supplierTypes',
        'vehicles'
      ],
      storage: localStorage
    }
  ]
})
